import { useCallback, useEffect } from "react";
import { useNotificationsStore } from "../store/notifications-store";
import { useToast } from "@/features/toast";
import { NotificationSettings as APINotificationSettings } from "@/api/models/api-types";
import useAuthStore from "@/store/use-auth-store";
import { useUpdateNotificationSettings } from "@/api/hooks/use-query-api";

export const useNotifications = () => {
  const { success, error } = useToast();

  // Auth Store
  const { user } = useAuthStore();

  // API Hook
  const updateNotificationMutation = useUpdateNotificationSettings();

  // Store Hook
  const {
    categories,
    notificationSettings,
    originalSettings,
    updateCategorySetting,
    resetNotificationSettings,
    setOriginalSettings,
    updateOriginalSettings,
  } = useNotificationsStore();

  // Load notification settings from API
  useEffect(() => {
    if (user?.notifications) {
      setOriginalSettings(user.notifications);
    }
  }, [user, setOriginalSettings]);

  // Business Actions
  const handleToggle = useCallback(
    (categoryId: string, settingType: "push" | "email" | "sms") => {
      const currentValue =
        notificationSettings[categoryId]?.[settingType] || false;
      updateCategorySetting(categoryId, settingType, !currentValue);
    },
    [notificationSettings, updateCategorySetting]
  );

  const handleSave = useCallback(async () => {
    try {
      // Convert local settings to API format
      const apiSettings: APINotificationSettings = {
        account: notificationSettings.account || {
          push: false,
          email: false,
          sms: false,
        },
        marketing: notificationSettings.marketing || {
          push: false,
          email: false,
          sms: false,
        },
        security: notificationSettings.security || {
          push: false,
          email: false,
          sms: false,
        },
        _id: user?.notifications?._id || "",
      };

      // Call API
      await updateNotificationMutation.mutateAsync({
        notifications: apiSettings,
      });

      // Update original settings after successful save
      updateOriginalSettings();
      success("Notification settings updated successfully");
    } catch (errorResponse: any) {
      console.error("Failed to save notification settings:", errorResponse);
      error("Failed to update notification settings");
    }
  }, [
    notificationSettings,
    user,
    updateNotificationMutation,
    updateOriginalSettings,
    success,
    error,
  ]);

  const handleCancel = useCallback(() => {
    // Reset to original settings
    resetNotificationSettings();
  }, [resetNotificationSettings]);

  // Computed values
  const hasChanges =
    JSON.stringify(notificationSettings) !== JSON.stringify(originalSettings);
  const isLoading = updateNotificationMutation.isPending;

  return {
    // Data
    categories,
    notificationSettings,
    originalSettings,

    // States
    isLoading,
    hasChanges,

    // Actions
    handleToggle,
    handleSave,
    handleCancel,
  };
};
